/**
 * gp-logcat 日志记录插件
 * @description UniApp Android 日志记录工具，支持本地文件存储和控制台输出
 * @version 1.0.0
 * @author Your Name
 * @license MIT
 */

import type { LogCatOptions, LogCatState, LogStatistics, LogFileInfo, LogEntry, LogWriteCallback, ILogCat, AndroidNativeClasses, CreateLogCatFn } from './gp-logcat-types';

import { LogLevel, LogLevelPriority } from './gp-logcat-types';

// 重新导出类型
export * from './gp-logcat-types';

/**
 * 默认配置
 */
const DEFAULT_OPTIONS: Required<LogCatOptions> = {
    saveDays: 14,
    filePrefix: 'log',
    fileExtension: '.txt',
    enableConsole: true,
    enableFileWrite: true,
    minLevel: LogLevel.DEBUG,
    tag: 'APP',
    logDirName: 'logs',
    maxFileSize: 5 * 1024 * 1024, // 5MB
    cleanOnInit: true,
    dateFormatter: (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    },
    dateTimeFormatter: (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hour = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        const second = String(date.getSeconds()).padStart(2, '0');
        const ms = String(date.getMilliseconds()).padStart(3, '0');
        return `${year}-${month}-${day} ${hour}:${min}:${second}.${ms}`;
    }
};

/**
 * 创建日志管理器实例
 * @param initOptions 初始化配置
 * @returns 日志管理器实例
 */
export const createLogCat: CreateLogCatFn = (initOptions?: LogCatOptions): ILogCat => {
    // 合并配置
    let options: Required<LogCatOptions> = { ...DEFAULT_OPTIONS, ...initOptions };

    // Android 原生类引用
    const native: Partial<AndroidNativeClasses> = {};

    // 状态
    let isInitialized = false;
    let logPath = '';
    let writeCallback: LogWriteCallback | null = null;

    /**
     * 格式化日期
     */
    const formatDate = (date: Date): string => {
        return options.dateFormatter(date);
    };

    /**
     * 格式化日期时间
     */
    const formatDateTime = (date: Date): string => {
        return options.dateTimeFormatter(date);
    };

    /**
     * 获取日志文件名
     */
    const getLogFileName = (date: Date, index = 0): string => {
        const dateStr = formatDate(date);
        const suffix = index > 0 ? `_${index}` : '';
        return `${options.filePrefix}_${dateStr}${suffix}${options.fileExtension}`;
    };

    /**
     * 序列化数据
     */
    const serializeData = (data: unknown): string => {
        if (data === undefined || data === null) {
            return '';
        }
        try {
            if (typeof data === 'string') {
                return data;
            }
            if (data instanceof Error) {
                return `${data.name}: ${data.message}\n${data.stack || ''}`;
            }
            return JSON.stringify(data, null, 0);
        } catch {
            return '[无法序列化的数据]';
        }
    };

    /**
     * 控制台输出
     */
    const consoleOutput = (level: LogLevel, tag: string, message: string, data?: unknown): void => {
        if (!options.enableConsole) return;

        const prefix = `[${tag}][${level}]`;
        const logMessage = `${prefix} ${message}`;

        switch (level) {
            case LogLevel.DEBUG:
                console.log(logMessage, data !== undefined ? data : '');
                break;
            case LogLevel.INFO:
                console.info(logMessage, data !== undefined ? data : '');
                break;
            case LogLevel.WARN:
                console.warn(logMessage, data !== undefined ? data : '');
                break;
            case LogLevel.ERROR:
                console.error(logMessage, data !== undefined ? data : '');
                break;
        }
    };

    /**
     * 初始化日志系统
     */
    const init = (newOptions?: LogCatOptions): boolean => {
        if (newOptions) {
            options = { ...options, ...newOptions };
        }

        try {
            // #ifdef APP-PLUS
            if (typeof plus !== 'undefined' && plus.android) {
                native.main = plus.android.runtimeMainActivity();
                native.Environment = plus.android.importClass('android.os.Environment');
                native.BufferedWriter = plus.android.importClass('java.io.BufferedWriter');
                native.File = plus.android.importClass('java.io.File');
                native.FileOutputStream = plus.android.importClass('java.io.FileOutputStream');
                native.OutputStreamWriter = plus.android.importClass('java.io.OutputStreamWriter');
                native.FileReader = plus.android.importClass('java.io.FileReader');
                native.BufferedReader = plus.android.importClass('java.io.BufferedReader');

                // 获取基础存储路径
                let basePath: string;
                if (native.Environment.MEDIA_MOUNTED || !native.Environment.isExternalStorageRemovable()) {
                    basePath = native.main.getExternalFilesDir(null).getPath();
                } else {
                    basePath = native.main.getFilesDir().getPath();
                }

                // 设置日志存储路径
                logPath = `${basePath}/${options.logDirName}`;

                // 确保日志目录存在
                const logDir = new native.File(logPath);
                if (!logDir.exists()) {
                    logDir.mkdirs();
                }

                isInitialized = true;

                // 清理过期日志
                if (options.cleanOnInit) {
                    cleanOldLogs();
                }

                console.log(`📝 [gp-logcat] 初始化成功`);
                console.log(`📝 [gp-logcat] 日志路径: ${logPath}`);
                console.log(`📝 [gp-logcat] 保留天数: ${options.saveDays}`);
                console.log(`📝 [gp-logcat] 最低级别: ${options.minLevel}`);

                return true;
            }
            // #endif

            // 非 APP-PLUS 环境
            console.warn('📝 [gp-logcat] 当前环境不支持文件写入，仅启用控制台输出');
            isInitialized = true;
            return true;
        } catch (error) {
            console.error('📝 [gp-logcat] 初始化失败:', error);
            return false;
        }
    };

    /**
     * 销毁日志系统
     */
    const destroy = (): void => {
        isInitialized = false;
        logPath = '';
        writeCallback = null;
        Object.keys(native).forEach((key) => {
            delete native[key as keyof AndroidNativeClasses];
        });
        console.log('📝 [gp-logcat] 已销毁');
    };

    /**
     * 获取当前状态
     */
    const getState = (): LogCatState => {
        return {
            isInitialized,
            logPath,
            options: { ...options }
        };
    };

    /**
     * 清理过期日志
     */
    const cleanOldLogs = (): number => {
        if (!logPath || !native.File) return 0;

        try {
            const logDir = new native.File(logPath);
            const files = logDir.listFiles();
            if (!files) return 0;

            const now = new Date();
            const cutoffDate = formatDate(new Date(now.getTime() - options.saveDays * 24 * 60 * 60 * 1000));
            let cleanedCount = 0;

            for (let i = 0; i < files.length; i++) {
                const fileName: string = files[i].getName();

                // 解析文件名中的日期
                const match = fileName.match(new RegExp(`^${options.filePrefix}_(\\d{4}-\\d{2}-\\d{2})`));
                if (match) {
                    const fileDate = match[1];
                    if (fileDate < cutoffDate) {
                        if (files[i].delete()) {
                            cleanedCount++;
                            console.log(`📝 [gp-logcat] 删除过期日志: ${fileName}`);
                        }
                    }
                }
            }

            return cleanedCount;
        } catch (error) {
            console.error('📝 [gp-logcat] 清理过期日志失败:', error);
            return 0;
        }
    };

    /**
     * 清理所有日志
     */
    const cleanAllLogs = (): number => {
        if (!logPath || !native.File) return 0;

        try {
            const logDir = new native.File(logPath);
            const files = logDir.listFiles();
            if (!files) return 0;

            let cleanedCount = 0;

            for (let i = 0; i < files.length; i++) {
                const fileName: string = files[i].getName();
                if (fileName.startsWith(options.filePrefix)) {
                    if (files[i].delete()) {
                        cleanedCount++;
                    }
                }
            }

            console.log(`📝 [gp-logcat] 已清理 ${cleanedCount} 个日志文件`);
            return cleanedCount;
        } catch (error) {
            console.error('📝 [gp-logcat] 清理所有日志失败:', error);
            return 0;
        }
    };

    /**
     * 获取日志统计信息
     */
    const getStatistics = (): LogStatistics | null => {
        if (!logPath || !native.File) return null;

        try {
            const logDir = new native.File(logPath);
            const files = logDir.listFiles();
            if (!files) {
                return {
                    logPath,
                    fileCount: 0,
                    totalSize: 0,
                    files: []
                };
            }

            const fileInfos: LogFileInfo[] = [];
            let totalSize = 0;
            let oldestDate: string | undefined;
            let newestDate: string | undefined;

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const fileName: string = file.getName();

                if (!fileName.startsWith(options.filePrefix)) continue;

                const size: number = file.length();
                const lastModified: number = file.lastModified();

                // 解析日期
                const match = fileName.match(new RegExp(`^${options.filePrefix}_(\\d{4}-\\d{2}-\\d{2})`));
                const date = match ? match[1] : '';

                if (date) {
                    if (!oldestDate || date < oldestDate) oldestDate = date;
                    if (!newestDate || date > newestDate) newestDate = date;
                }

                totalSize += size;
                fileInfos.push({
                    name: fileName,
                    path: file.getAbsolutePath(),
                    size,
                    date,
                    lastModified
                });
            }

            // 按日期排序
            fileInfos.sort((a, b) => b.date.localeCompare(a.date));

            return {
                logPath,
                fileCount: fileInfos.length,
                totalSize,
                oldestLogDate: oldestDate,
                newestLogDate: newestDate,
                files: fileInfos
            };
        } catch (error) {
            console.error('📝 [gp-logcat] 获取统计信息失败:', error);
            return null;
        }
    };

    /**
     * 读取日志文件
     */
    const readLog = (date: string): string | null => {
        if (!logPath || !native.File || !native.FileReader || !native.BufferedReader) {
            return null;
        }

        try {
            const fileName = `${logPath}/${options.filePrefix}_${date}${options.fileExtension}`;
            const file = new native.File(fileName);

            if (!file.exists()) {
                return null;
            }

            let reader = null;
            let bufferedReader = null;
            const lines: string[] = [];

            try {
                reader = new native.FileReader(file);
                bufferedReader = new native.BufferedReader(reader);

                let line: string | null;
                while ((line = bufferedReader.readLine()) !== null) {
                    lines.push(line);
                }
            } finally {
                if (bufferedReader) bufferedReader.close();
                if (reader) reader.close();
            }

            return lines.join('\n');
        } catch (error) {
            console.error('📝 [gp-logcat] 读取日志失败:', error);
            return null;
        }
    };

    /**
     * 读取今天的日志
     */
    const readTodayLog = (): string | null => {
        return readLog(formatDate(new Date()));
    };

    /**
     * 导出日志文件路径
     */
    const exportLogs = (dates?: string[]): string[] => {
        if (!logPath || !native.File) return [];

        try {
            const logDir = new native.File(logPath);
            const files = logDir.listFiles();
            if (!files) return [];

            const paths: string[] = [];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const fileName: string = file.getName();

                if (!fileName.startsWith(options.filePrefix)) continue;

                if (dates && dates.length > 0) {
                    const match = fileName.match(new RegExp(`^${options.filePrefix}_(\\d{4}-\\d{2}-\\d{2})`));
                    if (!match || !dates.includes(match[1])) continue;
                }

                paths.push(file.getAbsolutePath());
            }

            return paths;
        } catch (error) {
            console.error('📝 [gp-logcat] 导出日志失败:', error);
            return [];
        }
    };

    /**
     * 写入日志
     */
    const writeLog = (level: LogLevel, message: string, data?: unknown): void => {
        // 检查日志级别
        if (LogLevelPriority[level] < LogLevelPriority[options.minLevel]) {
            return;
        }

        const now = new Date();
        const datetime = formatDateTime(now);
        const tag = options.tag;

        // 控制台输出
        consoleOutput(level, tag, message, data);

        // 创建日志条目
        const entry: LogEntry = {
            timestamp: datetime,
            level,
            message,
            tag,
            data
        };

        // #ifdef APP-PLUS
        if (!options.enableFileWrite || !isInitialized || !logPath) {
            writeCallback?.(entry, false);
            return;
        }

        try {
            // 序列化数据
            const dataStr = serializeData(data);

            // 构建日志内容
            const content = `${datetime} [${tag}][${level}] ${message}${dataStr ? ' | ' + dataStr : ''}\n`;

            // 获取或创建日志文件
            const fileName = `${logPath}/${getLogFileName(now)}`;

            // 确保目录存在
            const logDir = new native.File!(logPath);
            if (!logDir.exists()) {
                logDir.mkdirs();
            }

            // 检查文件大小，如需要则创建新分片
            let actualFileName = fileName;
            const baseFile = new native.File!(fileName);
            if (baseFile.exists() && baseFile.length() >= options.maxFileSize) {
                // 查找下一个可用的分片索引
                let index = 1;
                while (true) {
                    const shardName = `${logPath}/${getLogFileName(now, index)}`;
                    const shardFile = new native.File!(shardName);
                    if (!shardFile.exists() || shardFile.length() < options.maxFileSize) {
                        actualFileName = shardName;
                        break;
                    }
                    index++;
                }
            }

            // 写入文件
            let fos = null;
            let bw = null;

            try {
                fos = new native.FileOutputStream!(actualFileName, true);
                bw = new native.BufferedWriter!(new native.OutputStreamWriter!(fos));
                bw.append(content);
                writeCallback?.(entry, true);
            } finally {
                if (bw) bw.close();
                if (fos) fos.close();
            }
        } catch (error) {
            console.error('📝 [gp-logcat] 写入日志失败:', error);
            writeCallback?.(entry, false);
        }
        // #endif
    };

    /**
     * 带标签的日志
     */
    const tagLog = (tag: string, level: LogLevel, message: string, data?: unknown): void => {
        const originalTag = options.tag;
        options.tag = tag;
        writeLog(level, message, data);
        options.tag = originalTag;
    };

    /**
     * 设置写入回调
     */
    const setWriteCallback = (callback: LogWriteCallback | null): void => {
        writeCallback = callback;
    };

    /**
     * 更新配置
     */
    const updateOptions = (newOptions: Partial<LogCatOptions>): void => {
        options = { ...options, ...newOptions };
    };

    // 返回日志管理器实例
    return {
        init,
        destroy,
        getState,
        getStatistics,
        cleanOldLogs,
        cleanAllLogs,
        readLog,
        readTodayLog,
        exportLogs,
        writeLog,
        debug: (message: string, data?: unknown) => writeLog(LogLevel.DEBUG, message, data),
        info: (message: string, data?: unknown) => writeLog(LogLevel.INFO, message, data),
        warn: (message: string, data?: unknown) => writeLog(LogLevel.WARN, message, data),
        error: (message: string, data?: unknown) => writeLog(LogLevel.ERROR, message, data),
        tag: tagLog,
        setWriteCallback,
        updateOptions,
        formatDate,
        formatDateTime
    };
};

/**
 * 默认日志实例（全局单例）
 */
export const LogCat = createLogCat();

/**
 * 快捷方法导出
 */
export const initLogCat = (options?: LogCatOptions): boolean => LogCat.init(options);
export const logDebug = (message: string, data?: unknown): void => LogCat.debug(message, data);
export const logInfo = (message: string, data?: unknown): void => LogCat.info(message, data);
export const logWarn = (message: string, data?: unknown): void => LogCat.warn(message, data);
export const logError = (message: string, data?: unknown): void => LogCat.error(message, data);
